import type { AnnotationCoords } from "@alaffia-technology-solutions/components";
import type {
  PDFAnnot,
  PDFHighlightCoords,
} from "@alaffia-technology-solutions/pdf-viewer";

import { mapRatioToHighlightCoords } from "@alaffia-technology-solutions/pdf-viewer";

interface ApplyAnnotationHighlightsDeps {
  previous: PDFAnnot[];
  annotations?: AnnotationCoords[];
  pageIndex?: number;
  addHighlight: (
    pageIndex: number,
    coords: PDFHighlightCoords[],
  ) => Promise<PDFAnnot | null>;
  getPageDimensions: (
    pageIndex: number,
  ) => Promise<{ width: number; height: number } | null | undefined>;
  removeHighlights: (highlights: PDFAnnot[]) => Promise<unknown>;
  isCurrent: () => boolean;
  commit: (highlights: PDFAnnot[]) => void;
}

/**
 * Applies the `annotations` highlights on the given page while guarding
 * against overlapping invocations. When a newer invocation supersedes this
 * one (via `isCurrent`), any highlights already created by this call are
 * removed before returning, and nothing is committed. The previous highlights
 * are cleared (via `commit([])`) before awaiting `removeHighlights` so a
 * concurrent newer invocation never removes them twice.
 */
export const applyAnnotationHighlights = async ({
  previous,
  annotations,
  pageIndex,
  addHighlight,
  getPageDimensions,
  removeHighlights,
  isCurrent,
  commit,
}: ApplyAnnotationHighlightsDeps) => {
  if (!isCurrent()) return;

  if (previous.length > 0) {
    commit([]);
    await removeHighlights(previous);
    if (!isCurrent()) return;
  }

  if (!annotations) return;

  const pageDimensions = await getPageDimensions(pageIndex ?? 0);
  if (!isCurrent()) return;
  if (!pageDimensions) return;

  const settled = await Promise.allSettled(
    annotations.map((p) =>
      addHighlight(pageIndex ?? 0, [
        mapRatioToHighlightCoords(p, pageDimensions),
      ]),
    ),
  );

  const committed = settled.flatMap((result) =>
    result.status === "fulfilled" && result.value != null ? [result.value] : [],
  );

  if (!isCurrent()) {
    if (committed.length > 0) {
      await removeHighlights(committed);
    }
    return;
  }

  commit(committed);
};
